import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { waLink, IMG, LOGO_LIGHT, SOCIAL } from "@/lib/content";
import { Reveal, Overline } from "@/components/site/Reveal";

export const Contact = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const year = new Date().getFullYear();

  return (
    <section id="contact" ref={ref} className="relative overflow-hidden bg-navy" data-testid="contact">
      <motion.div style={{ y }} className="absolute inset-0 opacity-30">
        <img src={IMG.heroTwilight} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-navy/70" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-40">
        <Reveal className="max-w-3xl">
          <Overline className="text-teak">{t.contact.overline}</Overline>
          <h2 className="mt-5 font-serif font-light text-canvas text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 max-w-lg font-light text-lg leading-relaxed text-canvas/75">{t.contact.desc}</p>

          <a
            href={waLink(t.waMsg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="group mt-10 inline-flex items-center gap-4 rounded-full bg-canvas px-8 py-5 text-navy transition-colors hover:bg-teak hover:text-canvas"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
            </svg>
            <span className="text-sm uppercase tracking-[0.15em] font-medium">{t.contact.cta}</span>
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </a>
        </Reveal>

        <div className="mt-24 flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-t border-canvas/15 pt-10">
          <div>
            <img src={LOGO_LIGHT} alt="La Rondine Charter" className="h-20 w-auto" />
            <p className="mt-3 text-sm text-canvas/60">{t.footer.tagline}</p>
          </div>
          <div className="text-sm text-canvas/70 space-y-3">
            <p data-testid="contact-location">{t.contact.location}</p>
            <a href={waLink(t.waMsg)} target="_blank" rel="noopener noreferrer" className="block hover:text-canvas" data-testid="footer-whatsapp-link">
              WhatsApp · +39 388 695 7133
            </a>
            <a href="mailto:larondinecharter@gmail.com" className="block hover:text-canvas" data-testid="footer-email-link">
              larondinecharter@gmail.com
            </a>
            <div className="flex items-center gap-3 pt-1">
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-canvas/25 text-canvas/80 transition-colors hover:bg-canvas hover:text-navy"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                  <path d="M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.1-1.1.1-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1-.1 1.2-.1 1.6-.1 4.7s0 3.5.1 4.7c.1 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1.1-.1 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c-.1-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-2.1-.4-1.2-.1-1.6-.1-4.7-.1zm0 3.1a4.9 4.9 0 110 9.8 4.9 4.9 0 010-9.8zm0 8.1a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zm6.2-8.3a1.15 1.15 0 11-2.3 0 1.15 1.15 0 012.3 0z" />
                </svg>
              </a>
              <a
                href={SOCIAL.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                aria-label="Facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-canvas/25 text-canvas/80 transition-colors hover:bg-canvas hover:text-navy"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                  <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-canvas/40">
          <p>© {year} La Rondine Charter. {t.footer.rights}.</p>
          <Link to="/privacy" className="text-canvas/50 underline underline-offset-4 hover:text-canvas" data-testid="footer-privacy-link">
            {t.legal.privacyLink}
          </Link>
        </div>
      </div>
    </section>
  );
};
